<template>
    <div class="wrapper w-100 h-100 d-flex justify-center align-center">
        <v-card class="login-card" elevation="6" rounded="xl">
            <v-card-text class="pa-8 pa-sm-10">
                <!-- Logo -->
                <div class="text-center mb-6">
                    <router-link class="d-inline-flex align-center text-decoration-none" to="/">
                        <v-img :src="logoFull" :width="200" draggable="false"/>
                        <span class="font-weight-bold font-italic text-h4 text-primary ms-1">Go</span>
                    </router-link>
                </div>

                <h2 class="text-h5 font-weight-bold text-center mb-4">Change Your Password</h2>

                <v-alert type="info" variant="tonal" class="mb-4" rounded="lg">
                    Your password was reset by an administrator. Please choose a new password to continue.
                </v-alert>

                <v-form ref="form" v-model="formValid" validate-on="input lazy" @submit.prevent="changePassword">
                    <v-text-field v-model="currentPassword" :rules="[vRules.required]"
                                  autocomplete="current-password" autofocus color="primary"
                                  density="comfortable" label="Current/Temporary Password"
                                  prepend-inner-icon="mdi-lock-outline"
                                  rounded="lg" type="password" variant="outlined"/>

                    <v-text-field v-model="newPassword" :rules="[vRules.required, vRules.minLength(8)]"
                                  autocomplete="new-password" color="primary" density="comfortable"
                                  label="New Password" prepend-inner-icon="mdi-lock-plus-outline"
                                  rounded="lg" type="password" variant="outlined"/>

                    <v-text-field v-model="confirmPassword"
                                  :rules="[vRules.required, v => v === newPassword || 'Passwords do not match']"
                                  autocomplete="new-password" color="primary" density="comfortable"
                                  label="Confirm New Password" prepend-inner-icon="mdi-lock-check-outline"
                                  rounded="lg" type="password" variant="outlined"/>

                    <v-btn :disabled="!formValid" :loading="processing" block color="primary"
                           rounded="lg" size="large" type="submit" class="mt-2">
                        Update Password
                    </v-btn>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- Footer -->
        <div class="login-footer text-caption text-white">
            &copy; {{ new Date().getFullYear() }} SimpliGPS Go by NextEra IoT
        </div>
    </div>
</template>

<script setup>
import {ref} from 'vue'
import router from "@/router"
import {send} from '@/composables/apiClient'
import logoFull from "@/assets/images/logo-full.png"
import {useSnackbarStore} from '@/stores/snackBarStore'
import {useAuthStore} from '@/stores/authStore'

const snackbar = useSnackbarStore()
const auth = useAuthStore()
const form = ref(null)
const formValid = ref(false)
const processing = ref(false)
const currentPassword = ref(null)
const newPassword = ref(null)
const confirmPassword = ref(null)

const changePassword = () => {
    processing.value = true
    send('put', 'users/password', {
        current_password: currentPassword.value,
        password: newPassword.value,
        password_confirmation: confirmPassword.value,
    }).then((response) => {
        if (response.data.success) {
            // Release the router trap before navigating, else beforeEach bounces
            // us straight back to this screen.
            auth.clearMustChange()
            snackbar.showSnackbar('Password updated successfully!')
            router.push({name: 'dashboard'})
        }
    }).catch((error) => {
        const msg = error.response?.data?.message ?? 'Failed to update password'
        snackbar.showSnackbar(msg, 'error')
    }).finally(() => processing.value = false)
}
</script>

<style scoped>
.wrapper {
    background: url('@/assets/images/login-bg.png') rgba(38, 35, 90, 0.65);
    background-blend-mode: multiply;
    background-size: cover;
    background-position: center;
    position: relative;
}

.login-card {
    width: 100%;
    max-width: 440px;
    margin: 16px;
}

.login-footer {
    position: absolute;
    bottom: 16px;
    opacity: 0.8;
}
</style>
